import { Box } from '@chakra-ui/react';
import { useState, useEffect, useMemo } from 'react';
import 'react-grid-layout/css/styles.css';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';

import './pagegrid.css';
import ExportInstruments from './Instruments';

const breakpoints = { lg: 480, xxs: 0 };
const cols = { lg: 12, xxs: 1 };
const rowHeight = 80;

export interface PageGridProperties {
  page: any;
  allInstruments: any[];
  allInstrumentsData?: any[];
}

const PageGrid = ({ page, allInstruments, allInstrumentsData }: PageGridProperties) => {
  const [currentBreakpoint, setCurrentBreakpoint] = useState<keyof typeof cols>('lg');
  const [savedLayout, setSavedLayout] = useState<Layouts>({});

  const getNextAvailablePosition = (
    layout: Layout[],
    columns: number,
    defaultSize = { w: 2, h: 3 },
  ) => {
    for (let y = 0; y < 20; y += 1) {
      for (let x = 0; x <= columns - defaultSize.w; x += 1) {
        const isOccupied = layout.some(
          (item) =>
            x < item.x + item.w &&
            x + defaultSize.w > item.x &&
            y < item.y + item.h &&
            y + defaultSize.h > item.y,
        );
        if (!isOccupied) {
          return { x, y, ...defaultSize };
        }
      }
    }
    return { x: 0, y: 0, ...defaultSize };
  };

  useEffect(() => {
    if (page?.instrument_layout) {
      setSavedLayout(page.instrument_layout);
    }
  }, [page]);

  useEffect(() => {
    setSavedLayout((previousLayout: any) => {
      const updatedLayouts = { ...previousLayout };

      Object.keys(cols).forEach((breakpoint) => {
        const existingLayout = updatedLayouts[breakpoint] || [];
        const newLayout = [...existingLayout];

        allInstruments.forEach((item) => {
          if (!existingLayout.some((layoutItem: any) => layoutItem.i === String(item.id))) {
            const position = getNextAvailablePosition(
              newLayout,
              cols[breakpoint as keyof typeof cols],
              {
                h: item.instrument_type === 'kpi' ? 2 : 3,
                w: item.instrument_type === 'table' ? 3 : 2,
              },
            );
            newLayout.push({
              i: String(item.id),
              ...position,
              minH: item.instrument_type === 'kpi' ? 2 : 3,
              minW: item.instrument_type === 'table' ? 3 : 2,
            });
          }
        });

        updatedLayouts[breakpoint] = newLayout;
      });

      return updatedLayouts;
    });
  }, [allInstruments]);

  // Calculate grid height based on layout with better accuracy
  const calculateGridHeight = () => {
    const layout = savedLayout[currentBreakpoint] || [];
    if (layout.length === 0) return 300; // Default height

    const maxY = Math.max(...layout.map((item) => item.y + item.h));
    // More generous calculation: rowHeight * rows + extra padding for margins
    const extraPadding = 60; // Extra space for margins, padding, and safety
    const calculatedHeight = (maxY + 1) * rowHeight + extraPadding;
    return Math.max(400, calculatedHeight) + 20; // Higher minimum height
  };

  const ResponsiveReactGridLayout = useMemo(() => WidthProvider(Responsive), []);

  return (
    <Box
      className={`page-grid-container page-${page.id}`}
      style={{ marginBottom: '40px' }}
      marginLeft={-5}
      marginRight={-5}
    >
      <ResponsiveReactGridLayout
        resizeHandles={[]}
        useCSSTransforms
        style={{
          // border: '2px solid #e0e0e0',
          borderRadius: '8px',
          padding: '10px',
          minHeight: calculateGridHeight(),
        }}
        className={`dashboard-grid-container page-grid-${page.id}`}
        allowOverlap={false}
        layouts={savedLayout}
        preventCollision={false}
        rowHeight={rowHeight}
        autoSize={true}
        isDraggable={false}
        isResizable={false}
        onBreakpointChange={(newBreakpoint) =>
          setCurrentBreakpoint(newBreakpoint as keyof typeof cols)
        }
        margin={[12, 12]}
        breakpoints={breakpoints}
        cols={cols}
      >
        {savedLayout[currentBreakpoint]?.map((layoutItem) => {
          // Find the corresponding instrument for this layout item
          if (!allInstruments) return null;
          if (!allInstrumentsData) return null;
          // if (!layoutItem.i) return null;
          const item = allInstruments?.find((data: any) => String(data.id) === layoutItem.i);
          if (!item) return null;

          // Find the corresponding data for this instrument
          const instrumentData = allInstrumentsData[item.id];

          return (
            <Box
              className='dashboard-item grid-item'
              key={`${page.id}-${item.id}`}
              data-grid={{ ...layoutItem, isResizable: false, isDraggable: false }}
              bg='white'
              border='1px solid #e0e0e0'
              borderColor='gray.200'
              borderRadius='md'
              p={4}
            >
              {instrumentData && (
                <ExportInstruments widget={item} output={instrumentData || null} />
              )}
              {!instrumentData && (
                <Box opacity={0.7}>
                  No data available for {item.instrument_type}, {item.id}
                </Box>
              )}
            </Box>
          );
        })}
      </ResponsiveReactGridLayout>
    </Box>
  );
};

export default PageGrid;
